import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Clock,
  MapPin,
  Linkedin,
  Twitter,
  Instagram,
  Github,
  ArrowRight,
  Loader2,
  MessageCircle,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";

const CONTACT_CHANNELS = [
  {
    icon: Mail,
    label: "Email Us",
    value: "hello@echowingsmedia.com",
    href: "mailto:hello@echowingsmedia.com",
  },
  {
    icon: Phone,
    label: "Call Us",
    value: "+1 (555) 894-3210",
    href: "tel:+15558943210",
  },
  {
    icon: Clock,
    label: "Business Hours",
    value: "Mon – Fri · 9:00 – 18:00 EST",
  },
  {
    icon: MapPin,
    label: "Studio",
    value: "Grand Rapids, MI — remote-first team",
  },
];

const SUBJECTS = [
  "New Project",
  "SEO / GEO Audit",
  "Pricing Question",
  "Partnership",
  "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1100));
    setLoading(false);
    toast.success("Message received — we'll be in touch within 24 hours.", {
      description: `Thanks ${form.name.split(" ")[0]}, a senior strategist will personally reply.`,
    });
    setForm({
      name: "",
      email: "",
      company: "",
      subject: SUBJECTS[0],
      message: "",
    });
  };

  return (
    <PageShell testId="contact-page">
      {/* Hero */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              <MessageCircle className="w-3.5 h-3.5" />
              Contact Us
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Let&apos;s start a{" "}
              <span className="text-gradient-brand">conversation.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              Tell us about your business and goals. A senior strategist will
              personally respond within 24 hours — no chatbots, no auto-replies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="section pt-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-8 items-start">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-3xl p-[1px] bg-gradient-to-br from-blue-600/40 via-cyan-400/30 to-transparent"
          >
            <div className="rounded-3xl bg-[#0a0a0a] p-8 md:p-10">
              <h2 className="font-display text-2xl md:text-3xl font-bold">
                Send us a message
              </h2>
              <p className="mt-2 text-sm text-white/55">
                Average response time: under 6 working hours.
              </p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Full Name"
                  id="name"
                  placeholder="Jane Carter"
                  value={form.name}
                  onChange={update("name")}
                />
                <Field
                  label="Email"
                  id="email"
                  type="email"
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={update("email")}
                />
                <Field
                  label="Company"
                  id="company"
                  placeholder="Acme Co."
                  value={form.company}
                  onChange={update("company")}
                />
                <div>
                  <label
                    htmlFor="subject"
                    className="text-xs font-semibold uppercase tracking-widest text-white/50"
                  >
                    Subject
                  </label>
                  <select
                    id="subject"
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-cyan-500/40 focus:outline-none rounded-xl px-4 py-3 text-white transition-colors"
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s} className="bg-[#0a0a0a]">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="message"
                  className="text-xs font-semibold uppercase tracking-widest text-white/50"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your business and what you're looking to achieve…"
                  rows={6}
                  className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-cyan-500/40 focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-7 inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-7 py-4 rounded-full hover:shadow-[0_0_40px_rgba(0,229,255,0.4)] transition-all duration-300 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-white/40">
                We respect your inbox — no spam, no newsletters without consent.
              </p>
            </div>
          </motion.form>

          {/* Info column */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 space-y-4"
          >
            {CONTACT_CHANNELS.map((c, i) => {
              const inner = (
                <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.025] p-5 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600/20 to-cyan-400/10 border border-white/10 flex items-center justify-center shrink-0">
                    <c.icon className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                      {c.label}
                    </div>
                    <div className="mt-1 font-semibold text-white text-sm truncate">
                      {c.value}
                    </div>
                  </div>
                </div>
              );
              return c.href ? (
                <a
                  key={c.label}
                  href={c.href}
                  data-testid={`contact-channel-${i}`}
                  className="block"
                >
                  {inner}
                </a>
              ) : (
                <div key={c.label} data-testid={`contact-channel-${i}`}>
                  {inner}
                </div>
              );
            })}

            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5">
              <div className="text-[10px] uppercase tracking-widest text-white/45 font-semibold mb-3">
                Follow EchoWings
              </div>
              <div className="flex items-center gap-2">
                {[
                  { Icon: Linkedin, label: "linkedin" },
                  { Icon: Twitter, label: "twitter" },
                  { Icon: Instagram, label: "instagram" },
                  { Icon: Github, label: "github" },
                ].map(({ Icon, label }) => (
                  <a
                    key={label}
                    href="#"
                    data-testid={`contact-social-${label}`}
                    aria-label={label}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </section>

      {/* Final reassurance bar */}
      <section className="section pt-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-blue-600/[0.08] via-cyan-400/[0.05] to-blue-600/[0.08] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold">
                Prefer a quick audit instead?
              </h3>
              <p className="mt-2 text-white/60">
                We&apos;ll review your site and send a tailored growth report
                within 24 hours.
              </p>
            </div>
            <a
              href="/book-audit"
              data-testid="contact-audit-cta"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all whitespace-nowrap"
            >
              Book Free Audit
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, id, ...rest }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-widest text-white/50"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={`contact-input-${id}`}
        className="mt-2 w-full bg-white/[0.04] border border-white/10 focus:border-cyan-500/40 focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors"
        {...rest}
      />
    </div>
  );
}
